'use client';

import React, { useMemo, useState, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { useCurrentUser } from '@/contexts/authentication/client';
import type { Character } from '../types';
import { useCharacters, useStoryCharacters } from '../hooks';
import { CharacterListItem } from './CharacterListItem';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/shared/components/ui/sheet';
import { Input } from '@/shared/components/ui/input';
import { Button } from '@/shared/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { Loader2, Search, X, BookOpen, User } from 'lucide-react';

interface CharacterPickerSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (character: Character) => void;
  /** Highlight the currently-linked character, if any. */
  currentCharacterId?: string;
  title?: string;
}

/**
 * Single-select character picker for the book hub — a right-side Sheet exactly
 * like the wizard selector (library / from-stories tabs, search, grouped story
 * sections), reusing the shared CharacterListItem. Picking a character fires
 * onSelect and closes; the caller turns that character's appearance into the
 * book reference.
 */
export function CharacterPickerSheet({
  isOpen,
  onClose,
  onSelect,
  currentCharacterId,
  title,
}: CharacterPickerSheetProps) {
  const t = useTranslations('stories.characters.selector');
  const tTabs = useTranslations('stories.characters.tabs');
  const { user } = useCurrentUser();
  const familyId = user?.familyId ?? undefined;

  const [tab, setTab] = useState<'library' | 'story'>('library');
  const [search, setSearch] = useState('');
  const [storyVisible, setStoryVisible] = useState(20);

  const { data: libraryChars = [], isFetching: libLoading } = useCharacters(
    isOpen ? familyId : undefined,
  );
  const { data: storyGroups = [], isFetching: storyLoading } = useStoryCharacters(
    isOpen ? familyId : undefined,
  );

  const matches = useCallback((c: Character, q: string) =>
    c.name.toLowerCase().includes(q) || !!c.physicalDescription?.toLowerCase().includes(q),
  []);

  const filteredLibrary = useMemo(() => {
    const q = search.trim().toLowerCase();
    const sorted = [...libraryChars].sort((a, b) => a.name.localeCompare(b.name));
    return q ? sorted.filter((c) => matches(c, q)) : sorted;
  }, [libraryChars, search, matches]);

  const filteredStoryGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return storyGroups;
    return storyGroups
      .map((g) => ({ ...g, characters: g.characters.filter((c) => matches(c, q)) }))
      .filter((g) => g.characters.length > 0);
  }, [storyGroups, search, matches]);

  const storyCount = useMemo(
    () => storyGroups.reduce((s, g) => s + g.characters.length, 0),
    [storyGroups],
  );
  const filteredStoryCount = useMemo(
    () => filteredStoryGroups.reduce((s, g) => s + g.characters.length, 0),
    [filteredStoryGroups],
  );

  const shownStoryGroups = useMemo(() => {
    const out: typeof filteredStoryGroups = [];
    let budget = storyVisible;
    for (const g of filteredStoryGroups) {
      if (budget <= 0) break;
      const slice = g.characters.slice(0, budget);
      out.push({ ...g, characters: slice });
      budget -= slice.length;
    }
    return out;
  }, [filteredStoryGroups, storyVisible]);
  const storyHasMore = filteredStoryCount > storyVisible;

  const handlePick = useCallback((character: Character) => {
    onSelect(character);
    onClose();
  }, [onSelect, onClose]);

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-xl flex flex-col" data-testid="character-picker-sheet">
        <SheetHeader>
          <SheetTitle>{title || t('title')}</SheetTitle>
        </SheetHeader>

        {/* Search — filters both tabs */}
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('searchPlaceholder')}
            className="pl-8"
            data-testid="character-picker-search"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label={t('clearSearch')}
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <Tabs value={tab} onValueChange={(v) => setTab(v as 'library' | 'story')} className="flex-1 min-h-0 flex flex-col">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="library" data-testid="picker-tab-library">{tTabs('library')}</TabsTrigger>
            <TabsTrigger value="story" data-testid="picker-tab-story">
              {tTabs('fromStories')}
              {storyCount > 0 && (
                <span className="ml-1.5 rounded-full bg-muted px-1.5 text-xs">{storyCount}</span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Library */}
          <TabsContent value="library" className="mt-4 flex-1 min-h-0 overflow-y-auto space-y-4">
            {libLoading && libraryChars.length === 0 ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>
            ) : filteredLibrary.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                {search ? t('noSearchResults') : t('noCharactersMessage')}
              </p>
            ) : (
              filteredLibrary.map((c) => (
                <CharacterListItem
                  key={c.id}
                  character={c}
                  isSelected={c.id === currentCharacterId}
                  isAtLimit={false}
                  onToggle={() => handlePick(c)}
                  t={t}
                  readOnly
                />
              ))
            )}
          </TabsContent>

          {/* From stories */}
          <TabsContent value="story" className="mt-4 flex-1 min-h-0 overflow-y-auto space-y-6">
            {storyLoading && storyGroups.length === 0 ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>
            ) : storyGroups.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="mb-4 rounded-full bg-muted p-6"><BookOpen className="h-12 w-12 text-muted-foreground" /></div>
                <p className="max-w-md text-muted-foreground">{tTabs('storyEmpty')}</p>
              </div>
            ) : filteredStoryGroups.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">{t('noSearchResults')}</p>
            ) : (
              <>
                {shownStoryGroups.map((group) => (
                  <section key={group.bookId} className="space-y-3">
                    <h4 className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                      <User className="h-3.5 w-3.5 shrink-0" />
                      <span className="truncate">
                        {group.bookTitle ? tTabs('fromBook', { title: group.bookTitle }) : tTabs('fromStories')}
                      </span>
                    </h4>
                    <div className="space-y-4">
                      {group.characters.map((c) => (
                        <CharacterListItem
                          key={c.id}
                          character={c}
                          isSelected={c.id === currentCharacterId}
                          isAtLimit={false}
                          onToggle={() => handlePick(c)}
                          t={t}
                          readOnly
                        />
                      ))}
                    </div>
                  </section>
                ))}
                {storyHasMore && (
                  <div className="flex justify-center">
                    <Button type="button" variant="outline" size="sm" onClick={() => setStoryVisible((v) => v + 20)}>
                      {tTabs('loadMore')}
                    </Button>
                  </div>
                )}
              </>
            )}
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}
